import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, from, lastValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PageContext, SiteSettings } from 'src/app/core/models/site.response';
import { IEmbedded } from 'src/app/core/models/umbraco.response';

import { UmbracoApiService } from './umbraco-api.service';

@Injectable({ providedIn: 'root' })
export class SiteService {
  private api = inject(UmbracoApiService);
  private activatedRoute = inject(ActivatedRoute);

  private _isSsr = false;

  siteSettings: SiteSettings;
  pageContext = new BehaviorSubject<PageContext | undefined | null>(undefined);

  get isInMaintenance(): boolean {
    return !this.isSsr && !!this.pageContext.getValue()?.maintenancePage;
  }

  get isSsr(): boolean {
    const params = this.activatedRoute.snapshot.queryParams;
    if (params?.ssr) {
      this._isSsr = true;
    }

    return this._isSsr;
  }

  get isInPreviewMode(): boolean {
    return this.activatedRoute.snapshot.queryParamMap.get('preview') === 'true';
  }

  loadSiteSettings(): Promise<IEmbedded<SiteSettings> | undefined> {
    return lastValueFrom(
      from(this.api.get<IEmbedded<SiteSettings>>('type?contentType=siteSettings')).pipe(
        tap((data) => {
          this.siteSettings = Object.assign(new SiteSettings(), data._embedded.content[0]);
        })
      )
    );
  }

  async loadPageContext(suppressAllErrorCodes = false): Promise<PageContext | undefined> {
    const pageContextUrl = this.siteSettings?.defaultPageContext._url;
    if (!pageContextUrl) {
      // Prevent an exception if the site settings isn't loaded so the barcode sheet can appear
      return Promise.resolve(undefined);
    }

    const data = await this.api.get<PageContext>(`url?url=${pageContextUrl}&depth=2`, undefined, suppressAllErrorCodes);
    this.pageContext.next(data ? Object.assign(new PageContext(), data) : null);
    return data;
  }

  clearContext(): void {
    this.pageContext.next(null);
  }
}
